const normalizeScopeId = (value) => String(value ?? "").trim();
const normalizeRoleKey = (value) => String(value ?? "").trim().toLowerCase();

const readStoredHierarchyValue = (key) => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    return String(window.localStorage.getItem(key) ?? "").trim();
  } catch (error) {
    console.error(`Failed to read stored hierarchy value for ${key}:`, error);
    return "";
  }
};

const getStoredHierarchyUser = () => ({
  district_id:
    readStoredHierarchyValue("district_id") ||
    readStoredHierarchyValue("districtId"),
  role:
    readStoredHierarchyValue("role") ||
    readStoredHierarchyValue("userRole"),
  station_id:
    readStoredHierarchyValue("station_id") ||
    readStoredHierarchyValue("stationId"),
  sub_division_id:
    readStoredHierarchyValue("sub_division_id") ||
    readStoredHierarchyValue("subDivisionId"),
});

const resolveHierarchyUser = (user = null) => {
  const storedUser = getStoredHierarchyUser();

  return {
    ...storedUser,
    ...(user && typeof user === "object" ? user : {}),
    district_id:
      normalizeScopeId(user?.district_id ?? user?.districtId) ||
      storedUser.district_id,
    role:
      normalizeScopeId(user?.role ?? user?.user_role ?? user?.role_key) ||
      storedUser.role,
    station_id:
      normalizeScopeId(user?.station_id ?? user?.stationId) ||
      storedUser.station_id,
    sub_division_id:
      normalizeScopeId(user?.sub_division_id ?? user?.subDivisionId) ||
      storedUser.sub_division_id,
  };
};

const ROLE_SCOPE_BY_KEY = {
  admin: "global",
  district_admin: "district",
  officer: "station",
  sdpo: "sub_division",
  station_admin: "station",
  station_user: "station",
  sub_division_admin: "sub_division",
  super_admin: "global",
  viewer: "station",
};

const inferScopeTypeFromRole = (role) => {
  const normalizedRole = normalizeRoleKey(role);

  if (!normalizedRole) {
    return "";
  }

  if (ROLE_SCOPE_BY_KEY[normalizedRole]) {
    return ROLE_SCOPE_BY_KEY[normalizedRole];
  }

  if (normalizedRole.includes("district")) {
    return "district";
  }

  if (
    normalizedRole.includes("sub_division") ||
    normalizedRole.includes("sub-division") ||
    normalizedRole.includes("subdivision") ||
    normalizedRole.includes("sdpo")
  ) {
    return "sub_division";
  }

  if (
    normalizedRole.includes("station") ||
    normalizedRole.includes("officer") ||
    normalizedRole.includes("viewer")
  ) {
    return "station";
  }

  return "";
};

export const getAssignableRoleScopeType = (role, assignableRoles = []) => {
  const matchedRole = (Array.isArray(assignableRoles) ? assignableRoles : []).find(
    (item) => normalizeRoleKey(item?.key) === normalizeRoleKey(role)
  );
  const assignableScopeType = normalizeRoleKey(matchedRole?.scope_type);

  if (assignableScopeType) {
    return assignableScopeType;
  }

  return inferScopeTypeFromRole(role);
};

export const roleNeedsDistrict = (role, assignableRoles = []) =>
  getAssignableRoleScopeType(role, assignableRoles) !== "global";

export const roleNeedsSubDivision = (role, assignableRoles = []) => {
  const scopeType = getAssignableRoleScopeType(role, assignableRoles);
  return scopeType === "sub_division" || scopeType === "station";
};

export const roleNeedsStation = (role, assignableRoles = []) =>
  getAssignableRoleScopeType(role, assignableRoles) === "station";

export const inferUserScopeType = (user = {}) => {
  const hierarchyUser = resolveHierarchyUser(user);
  const roleScopeType = inferScopeTypeFromRole(hierarchyUser.role);

  if (roleScopeType) {
    return roleScopeType;
  }

  if (normalizeScopeId(hierarchyUser.station_id)) {
    return "station";
  }

  if (normalizeScopeId(hierarchyUser.sub_division_id)) {
    return "sub_division";
  }

  if (normalizeScopeId(hierarchyUser.district_id)) {
    return "district";
  }

  return "global";
};

const buildHierarchyAccess = ({ enabled = false, user = null } = {}) => {
  const hierarchyUser = resolveHierarchyUser(user);
  const fixedDistrictId = normalizeScopeId(hierarchyUser.district_id);
  const fixedSubDivisionId = normalizeScopeId(hierarchyUser.sub_division_id);
  const fixedStationId = normalizeScopeId(hierarchyUser.station_id);
  const scopeType = inferUserScopeType(hierarchyUser);
  const canSelectDistrict = enabled && scopeType === "global";
  const canSelectSubDivision =
    enabled && (scopeType === "global" || scopeType === "district");
  const canSelectStation =
    enabled &&
    (scopeType === "global" ||
      scopeType === "district" ||
      scopeType === "sub_division");

  return {
    canSelectDistrict,
    canSelectStation,
    canSelectSubDivision,
    fixedDistrictId,
    fixedStationId,
    fixedSubDivisionId,
    normalizeScopeId,
    scopeType,
    showDistrictField: canSelectDistrict,
    showStationField: canSelectStation,
    showSubDivisionField: canSelectSubDivision,
  };
};

export const getHierarchyScopeAccess = ({
  canReadAdminReports = false,
  capabilitiesReady = false,
  user = null,
} = {}) => {
  const canUseAdminHierarchy = Boolean(capabilitiesReady && canReadAdminReports);

  return {
    ...buildHierarchyAccess({
      enabled: canUseAdminHierarchy,
      user,
    }),
    canUseAdminHierarchy,
  };
};

export const getMasterHierarchyAccess = ({ user = null } = {}) =>
  buildHierarchyAccess({
    enabled: true,
    user,
  });

export const buildScopeFromHierarchySelection = ({
  canSelectDistrict = false,
  canSelectStation = false,
  canSelectSubDivision = false,
  districtId = "",
  fixedDistrictId = "",
  fixedStationId = "",
  fixedSubDivisionId = "",
  includeUser = false,
  normalizeValue = normalizeScopeId,
  stationId = "",
  subDivisionId = "",
  userId = "",
} = {}) => {
  const nextScope = {};
  const nextDistrictId = canSelectDistrict
    ? normalizeValue(districtId)
    : fixedDistrictId;
  const nextSubDivisionId = canSelectSubDivision
    ? normalizeValue(subDivisionId)
    : fixedSubDivisionId;
  const nextStationId = canSelectStation
    ? normalizeValue(stationId)
    : fixedStationId;

  if (nextDistrictId) {
    nextScope.districtId = nextDistrictId;
  }

  if (nextSubDivisionId) {
    nextScope.subDivisionId = nextSubDivisionId;
  }

  if (nextStationId) {
    nextScope.stationId = nextStationId;
  }

  if (includeUser) {
    const nextUserId = normalizeValue(userId);

    if (nextUserId) {
      nextScope.userIds = [nextUserId];
    }
  }

  return nextScope;
};

const getOptionValue = (item = {}) =>
  String(item?.id ?? item?.user_id ?? item?.value ?? "").trim();

const getOptionLabel = (item = {}) =>
  item?.name ||
  item?.label ||
  item?.user_name ||
  item?.username ||
  item?.email ||
  getOptionValue(item);

export const ensureFixedOption = (items = [], value = "", fallbackLabel = "") => {
  const normalizedValue = normalizeScopeId(value);

  if (!normalizedValue) {
    return Array.isArray(items) ? items : [];
  }

  const safeItems = Array.isArray(items) ? items : [];
  const existingItem = safeItems.find(
    (item) => getOptionValue(item) === normalizedValue
  );

  if (existingItem) {
    return safeItems;
  }

  return [
    {
      id: normalizedValue,
      label: fallbackLabel || normalizedValue,
      name: fallbackLabel || normalizedValue,
      value: normalizedValue,
    },
    ...safeItems,
  ];
};

export const resolveSelectedOptionLabel = (items = [], value = "", fallbackLabel = "") => {
  const normalizedValue = normalizeScopeId(value);

  if (!normalizedValue) {
    return fallbackLabel;
  }

  const matchedItem = (Array.isArray(items) ? items : []).find(
    (item) => getOptionValue(item) === normalizedValue
  );

  return matchedItem ? getOptionLabel(matchedItem) : fallbackLabel || normalizedValue;
};
